import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { dosliBrakujaceWiersze, wynikOdczytuRozjazdu, zlecOdczytRozjazdu, type WynikOdczytuRozjazdu } from '@/lib/machines-api';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ODCZYT JEDNEJ TABELI Z WSZYSTKICH BAZ WYSPY — przycisk, wynik i dosyłka brakujących.
 *
 *  ⚠️⚠️ ZAMÓWIONE 09.09.2026: „przycisk, który faktycznie odpyta te bazy selectem i wypluje
 *  dokładny rozjazd? Bo to strasznie dużo pierdolenia logować się teraz do każdej bazy i szukać
 *  SSMS, gdy agent to wszystko może robić".
 *
 *  ⚠️⚠️ WYDZIELONE Z `RozjazdSzczegoly` 14.09.2026, gdy ten sam blok był potrzebny DRUGI RAZ —
 *  do kadry (`Users` i członkostwa), której czujnik geometrii nie widzi w ogóle. Kopia tych stu
 *  linijek rozjechałaby się przy pierwszej poprawce, a rozjazd między dwoma ekranami mówiącymi
 *  o tym samym jest dokładnie tą chorobą, którą leczy całe to okno.
 *
 *  ⚠️ WYNIK PRZYCHODZI PO CYKLU AGENTA — stąd stan „czekam”, a nie kręcące się kółko bez końca.
 *  Zlecenie jest jednym zadaniem na węzeł, więc widać też, który węzeł nie odpowiedział: bez tego
 *  „brakuje u magna2" mogłoby znaczyć „magna2 milczy".
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function OdczytTabeli({
  groupId,
  tabela,
  /** `null` = tabela wspólna wyspy (kadra) — odczyt bez numeru automatu. */
  vmid,
  onWynik,
}: {
  groupId: string;
  tabela: string;
  vmid: number | null;
  /** ⚠️ Wynik idzie też W GÓRĘ, bo plik z rozjazdami składa się z tych samych odczytów, co ekran. */
  onWynik?: (tabela: string, vmid: number | null, wynik: WynikOdczytuRozjazdu) => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const [o, setO] = useState<WynikOdczytuRozjazdu | null>(null);
  const [czeka, setCzeka] = useState(false);
  const [dosylane, setDosylane] = useState(false);
  /**
   * ⚠️⚠️ WĘZŁY POMINIĘTE ZE WZGLĘDU NA WERSJĘ AGENTA. Wynik składa się z tych, które
   * ODPOWIEDZIAŁY — więc węzeł, którego nie zapytano, wyglądałby jak węzeł bez braków.
   * „Nie wiemy" musi wyglądać inaczej niż „zgodne".
   */
  const [zaStare, setZaStare] = useState<{ wezly: string[]; wersja: string } | null>(null);

  /**
   * ⚠️⚠️ WYWOŁANIE ZWROTNE TRZYMAMY W REFERENCJI, a nie w zależnościach efektu. Rodzic podaje je
   * zwykle jako funkcję pisaną w miejscu, więc każde jego przerysowanie zrywałoby `setInterval`
   * i zakładało nowy — czyli dopytywanie co 4 s zamieniłoby się w „nigdy", gdyby ekran odświeżał
   * się częściej. Ta pułapka nie daje o sobie znać na spokojnym ekranie i wychodzi dopiero
   * u klienta.
   */
  const naWynik = useRef(onWynik);
  naWynik.current = onWynik;

  const zlec = async () => {
    setCzeka(true);
    try {
      const w = await zlecOdczytRozjazdu(groupId, tabela, vmid);
      setZaStare(
        w.zaStare != null && w.zaStare.length > 0
          ? { wezly: w.zaStare, wersja: w.wersjaWymagana ?? '—' }
          : null,
      );
    } catch (e) {
      setCzeka(false);
      toast.error(e instanceof ApiError ? e.message : 'Nie udało się zlecić odczytu');
    }
  };

  /**
   * ⚠️⚠️ DOPYTUJEMY TYLKO WTEDY, GDY NA COŚ CZEKAMY — i przestajemy, gdy wynik wrócił. Stałe
   * odświeżanie ekranu, który nikogo nie pyta, to jedno żądanie na kilka sekund do końca życia
   * karty, a ta bywa otwarta przez całe wdrożenie.
   */
  useEffect(() => {
    if (!czeka) return;
    const timer = setInterval(() => {
      void wynikOdczytuRozjazdu(groupId, tabela, vmid)
        .then((w) => {
          setO(w);
          if (w.stan !== 'CZEKA') {
            setCzeka(false);
            naWynik.current?.(tabela, vmid, w);
          }
        })
        .catch(() => undefined);
    }, 4000);
    return () => clearInterval(timer);
  }, [czeka, groupId, tabela, vmid]);

  /**
   * DOSŁANIE BRAKUJĄCYCH WIERSZY — druga decyzja, po przejrzeniu odczytu.
   *
   * ⚠️ ODMOWA NIE JEST BŁĘDEM. „Nie ma odczytu baz", „odczyt obcięty limitem" i „nic nie brakuje"
   * to normalne stany tego przycisku; serwer oddaje wtedy `200` ze zdaniem w `powod`.
   */
  const dosli = async () => {
    setDosylane(true);
    try {
      const w = await dosliBrakujaceWiersze(groupId, tabela, vmid);
      if (w.zakolejkowanych === 0) toast.toast(w.powod ?? t('rozj.dosliNic'), 'info');
      else {
        toast.success(t('rozj.dosliWynik').replace('{n}', String(w.zakolejkowanych)));
        void qc.invalidateQueries({ queryKey: ['changes'] });
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Nie udało się dosłać wierszy');
    } finally {
      setDosylane(false);
    }
  };

  return (
    <div className="ml-1 space-y-1">
      <Button
        size="sm"
        variant="ghost"
        className="h-6 px-1 text-[11px]"
        disabled={czeka}
        onClick={() => void zlec()}
        title={t('rozj.odpytajOpis')}
      >
        {czeka ? <Loader2 className="size-3 animate-spin" /> : <Search className="size-3" />}
        {czeka ? t('rozj.odpytajCzekam') : t('rozj.odpytaj')}
      </Button>

      {zaStare !== null && (
        <p className="text-[11px] text-[var(--warning)]">
          {t('rozj.zaStareWezly').replace('{wezly}', zaStare.wezly.join(', ')).replace('{wersja}', zaStare.wersja)}
        </p>
      )}

      {o !== null && o.stan === 'GOTOWE' && (
        <div className="space-y-1 rounded border border-border bg-muted/20 p-2 text-[11px]">
          {/*
            ⚠️⚠️ STAN WĘZŁÓW NAD WYNIKIEM. Odczyt z trzech baz na cztery nadal jest odpowiedzią,
            ale ekran musi powiedzieć, że jednej brakuje — inaczej „brakuje u magna2" czytałoby się
            jako różnica, a nie jako brak odpowiedzi.
          */}
          <p className="text-muted-foreground">
            {o.wezly
              .map((w) =>
                w.blad !== null ? `${w.nodeId}: ${t('rozj.odczytBlad')}` : `${w.nodeId}: ${w.wierszy ?? 0}${w.obcietych > 0 ? '+' : ''}`,
              )
              .join(' · ')}
          </p>

          {/*
            ══════════════════════════════════════════════════════════════════════════════════════
             PORTAL JAKO STRONA PORÓWNANIA — linia nad wynikiem, nie pod nim.

             ⚠️⚠️ ZAMÓWIONE PO AWARII IKEA (21.09.2026): „sync między masterem a maszynami w teorii
             był, a postgres był rozjebany". Zmierzone wtedy: ZK1 80,0%, master 81,1%, portal 62,4%.
             Bazy klienta rozmawiały ze sobą poprawnie, a ten ekran odpowiadał „zgodne" — prawdę,
             i zupełnie bezużyteczną, bo pytanie brzmiało, czy PORTAL ma to samo.

             ⚠️ NAD wynikiem porównania węzłów, bo „portalowi brakuje 2700 wierszy" zmienia
             znaczenie wszystkiego, co jest niżej: zgodne węzły przy rozjechanym portalu to nie
             jest zdrowa wyspa, tylko zdrowa wyspa i chory read-model.
          */}
          {o.portal === null && (
            <p className="text-muted-foreground">{t('rozj.portalNieUmie')}</p>
          )}

          {o.portal != null && (
            <p
              className={
                o.portal.brakujeWPortalu > 0 || o.portal.nadmiarWPortalu > 0
                  ? 'font-medium text-[var(--warning)]'
                  : 'text-muted-foreground'
              }
            >
              {t('rozj.portalWierszy').replace('{n}', String(o.portal.wierszy))}
              {o.portal.brakujeWPortalu > 0 &&
                ` · ${t('rozj.portalBrak').replace('{n}', String(o.portal.brakujeWPortalu))}`}
              {o.portal.nadmiarWPortalu > 0 &&
                ` · ${t('rozj.portalNadmiar').replace('{n}', String(o.portal.nadmiarWPortalu))}`}
            </p>
          )}

          {/* ⚠️ Obcięty odczyt podważa WYŁĄCZNIE nadmiar — brak w portalu jest faktem mimo obcięcia. */}
          {o.portal?.niepewne === true && (
            <p className="text-[11px] text-muted-foreground">{t('rozj.portalNiepewne')}</p>
          )}

          {o.portal != null && o.portal.przykladyBrakuje.length > 0 && (
            <p className="break-all text-[11px]">
              <span className="text-muted-foreground">{t('rozj.portalBrakPrzyklady')}</span>{' '}
              <span className="font-mono">{o.portal.przykladyBrakuje.join(' · ')}</span>
            </p>
          )}

          {o.portal != null && o.portal.przykladyNadmiar.length > 0 && (
            <p className="break-all text-[11px]">
              <span className="text-muted-foreground">{t('rozj.portalNadmiarPrzyklady')}</span>{' '}
              <span className="font-mono">{o.portal.przykladyNadmiar.join(' · ')}</span>
            </p>
          )}

          {/*
            ⚠️⚠️ „ZGODNE" MUSI TERAZ ZNACZYĆ ZGODNE Z PORTALEM TEŻ. Zielone zdanie przy rozjechanym
            read-modelu byłoby dokładnie tym fałszywym spokojem, dla którego ta linia powstała.
          */}
          {o.brakujace.length === 0 &&
            o.rozne.length === 0 &&
            (o.portal == null || (o.portal.brakujeWPortalu === 0 && o.portal.nadmiarWPortalu === 0)) && (
              <p className="text-[var(--success)]">{t('rozj.odczytZgodne').replace('{n}', String(o.kluczyRazem))}</p>
            )}

          {o.brakujace.slice(0, 40).map((b) => (
            <p key={`brak-${b.klucz}`} className="break-all">
              <span className="font-mono text-[var(--destructive)]">{b.klucz}</span>{' '}
              <span className="text-muted-foreground">
                {t('rozj.odczytBrak').replace('{nieMaja}', b.nieMaja.join(', '))}
              </span>{' '}
              <span className="font-mono">{opisWartosci(b.wartosci)}</span>
            </p>
          ))}

          {o.rozne.slice(0, 40).map((x) => (
            <p key={`rozne-${x.klucz}`} className="break-all">
              <span className="font-mono text-[var(--warning)]">{x.klucz}</span>{' '}
              {x.kolumny.map((kol) => (
                <span key={kol.kolumna} className="font-mono">
                  {kol.kolumna}: {kol.grupy.map((g) => `${g.wartosc ?? 'NULL'} (${g.wezly.join(', ')})`).join(' ≠ ')}{' '}
                </span>
              ))}
            </p>
          ))}

          {/* ⚠️ LISTA UCIĘTA MUSI SIĘ PRZYZNAĆ — pełna jest w pliku. */}
          {(o.brakujace.length > 40 || o.rozne.length > 40) && (
            <p className="text-muted-foreground">{t('rozj.odczytWiecej')}</p>
          )}

          {/*
            ⚠️⚠️ TYLKO GDY CZEGOŚ BRAKUJE. Przy samych różnicach treści (`rozne`) dosyłanie nie ma
            nic do zrobienia — wiersz istnieje u wszystkich, a wypełnienie przechodzi obok
            istniejących. Pokazanie przycisku wtedy obiecywałoby naprawę, której nie ma.
          */}
          {/*
            ⚠️⚠️ LICZBA I DECYZJA Z PLANU SERWERA (`o.doslanie`), NIE Z `brakujace.length`. Od 23.09.2026
            przy tabeli jednego automatu nie jadą pozostałości w kopiach, a od 24.09.2026 (wariant B) także
            przegródki zajęte u każdego adresata innym towarem — przycisk, który obiecuje więcej, niż akcja
            zrobi, uczy patrzącego, że ekran kłamie. Gdy nie jedzie nic, zamiast przycisku stoi powód.
            `?? ` na wypadek starszej instancji, która planu nie odda.
          */}
          {o.brakujace.length > 0 &&
            (o.doslanie != null && !o.doslanie.mozliwe ? (
              <p className="text-[11px] text-muted-foreground">{o.doslanie.powod}</p>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 px-2 text-[11px]"
                  disabled={dosylane}
                  onClick={() => void dosli()}
                  title={t('rozj.dosliOpis')}
                >
                  {dosylane && <Loader2 className="size-3 animate-spin" />}
                  {t('rozj.dosli').replace('{n}', String(o.doslanie?.wierszy ?? o.brakujace.length))}
                </Button>
                {(o.doslanie?.tylkoWKopiach ?? 0) > 0 && (
                  <span className="text-[11px] text-muted-foreground">
                    {t('rozj.tylkoWKopiach').replace('{n}', String(o.doslanie!.tylkoWKopiach))}
                  </span>
                )}
                {(o.doslanie?.zajetych ?? 0) > 0 && (
                  <span className="text-[11px] text-muted-foreground">
                    {t('rozj.zajete').replace('{n}', String(o.doslanie!.zajetych))}
                  </span>
                )}
              </div>
            ))}
        </div>
      )}
      {o !== null && o.stan === 'CZEKA' && <p className="text-[11px] text-muted-foreground">{t('rozj.odczytCzeka')}</p>}
    </div>
  );
}

/**
 * WARTOŚCI WIERSZA W JEDNEJ LINII — tyle, ile pozwala szerokość okna.
 *
 * ⚠️⚠️ POKAZUJEMY KILKA PIERWSZYCH KOLUMN, A NIE WSZYSTKIE. Wiersz `DrawerCompartments` ma
 * kilkanaście kolumn i w oknie zamieniłby się w ścianę tekstu; całość jest w pliku, który
 * ten sam ekran zapisuje. Napis kończy się wielokropkiem, gdy coś zostało — bez tego czytałoby
 * się jako pełna treść wiersza.
 */
const KOLUMN_W_LINII = 4;

export function opisWartosci(w: Record<string, string | null>): string {
  const pary = Object.entries(w);
  const widoczne = pary.slice(0, KOLUMN_W_LINII).map(([k, v]) => `${k}=${v ?? 'NULL'}`);
  return `{ ${widoczne.join(', ')}${pary.length > KOLUMN_W_LINII ? ', …' : ''} }`;
}
